// HTTP client — single source of truth for talking to the backend.
//
// Why this exists:
//  - Every /api/* call needs `credentials: 'include'` so the gateway cookie
//    travels cross-origin from studio.overdigital.ai → create.overdigital.ai.
//  - On 401, the user must be bounced to the gateway login with a return URL.
//  - We want a uniform throw-on-non-2xx contract so callers don't each invent
//    their own error handling.
//
// Do not call fetch() directly elsewhere in the SPA.

// Trim defensively — Cloudflare Pages env vars have bitten us with leading
// whitespace that turns `fetch(" https://…/api/me")` into a broken request.
const API_BASE = (import.meta.env.VITE_API_BASE || 'https://create.overdigital.ai').trim();
const LOGIN_BASE = 'https://create.overdigital.ai';

class ApiError extends Error {
  constructor(status, body, url) {
    super(`${status} ${typeof body === 'string' ? body : (body?.error || 'API error')} (${url})`);
    this.status = status;
    this.body = body;
    this.url = url;
  }
}

function loginRedirect() {
  const here = window.location.href;
  window.location.href = `${LOGIN_BASE}/__auth/login?redirect=${encodeURIComponent(here)}`;
}

async function request(method, path, { body, headers, signal, raw } = {}) {
  const url = (API_BASE && !path.startsWith('http')) ? `${API_BASE}${path}` : path;
  const init = {
    method,
    credentials: 'include',
    headers: { 'Accept': 'application/json', ...(headers || {}) },
    signal
  };
  if (body !== undefined) {
    if (body instanceof FormData) {
      init.body = body; // browser sets multipart boundary
    } else {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
  }

  const res = await fetch(url, init);

  if (res.status === 401) {
    loginRedirect();
    throw new ApiError(401, 'Not authenticated', url);
  }

  if (raw) return res;

  const ct = res.headers.get('content-type') || '';
  const payload = ct.includes('application/json') ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) throw new ApiError(res.status, payload, url);
  return payload;
}

export const api = {
  get:    (path, opts)        => request('GET', path, opts),
  post:   (path, body, opts)  => request('POST', path, { ...(opts || {}), body }),
  put:    (path, body, opts)  => request('PUT', path, { ...(opts || {}), body }),
  delete: (path, opts)        => request('DELETE', path, opts),
  raw:    (path, opts)        => request('GET', path, { ...(opts || {}), raw: true })
};

// ── QR-Offer-Pro (vendored FastAPI in vendor/QR-Offer-Pro) ─────────────────
// Dev: Vite proxies /qr-discount → http://127.0.0.1:8000 (run npm run qr-offer:dev).
// Prod: set VITE_QR_OFFER_URL to the deployed service origin (no trailing slash).
const QR_OFFER_ENV = (import.meta.env.VITE_QR_OFFER_URL || '').trim();

function qrOfferBase() {
  if (QR_OFFER_ENV) return QR_OFFER_ENV.replace(/\/$/, '');
  if (import.meta.env.DEV) return '/qr-discount';
  return '';
}

async function qrOfferRequest(method, path, { body, headers, signal, raw } = {}) {
  const base = qrOfferBase();
  if (!base) {
    throw new Error(
      'QR Offer API unavailable: in dev run `npm run qr-offer:dev`; in prod set VITE_QR_OFFER_URL.'
    );
  }
  const pathPart = path.startsWith('/') ? path : `/${path}`;
  const url = base.startsWith('http') ? `${base}${pathPart}` : `${base}${pathPart}`;

  const init = {
    method,
    headers: { Accept: 'application/json', ...(headers || {}) },
    signal,
    credentials: base.startsWith('http') ? 'omit' : 'same-origin',
  };
  if (body !== undefined) {
    if (body instanceof FormData) {
      init.body = body;
    } else {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
  }

  const res = await fetch(url, init);
  if (raw) return res;

  const ct = res.headers.get('content-type') || '';
  const payload = ct.includes('application/json') ? await res.json().catch(() => null) : await res.text();
  if (!res.ok) throw new ApiError(res.status, payload, url);
  return payload;
}

/** Client for vendor/QR-Offer-Pro (discount codes + QR). Not the Create Studio backend. */
export const qrOfferApi = {
  get:   (path, opts)       => qrOfferRequest('GET', path, opts),
  post:  (path, body, opts) => qrOfferRequest('POST', path, { ...(opts || {}), body }),
  raw:   (path, opts)       => qrOfferRequest('GET', path, { ...(opts || {}), raw: true }),
};

export { ApiError, loginRedirect };
